import React from 'react';
import { TPropertiesOfPatchChangesForModifiers } from 'types';
import CronGenerator from 'components/CronGenerator/CronGenerator';
import Text from 'components/Text/Text';
import breadcrumbs from 'assets/img/breadcrumbs.svg';
import { PriceInput } from './PriceInput';
import { SupplementPriceInput } from './SupplementPriceInput';
import { QuantityMaxLimitInput } from './QuantityMaxLimitInput';
import { InStopCheckbox } from './InStopCheckbox';
import { IsRestrictedCheckbox } from './IsRestrictedCheckbox';
import { IsPreselectedCheckbox } from './IsPreselectedCheckbox';
import { ForceDisableCheckbox } from './ForceDisableCheckbox';
import { ActivationScheduleInput } from './ActivationScheduleInput';
import { DeactivationScheduleInput } from './DeactivationScheduleInput';
import style from './Cron.module.scss';

interface IProps {
	changePrice(value: Nullable<number>): void;
	changeSupplementPrice(value: Nullable<number>): void;
	changeQuantityMaxLimit(value: Nullable<number>): void;
	changeActivationSchedule(value: string): void;
	changeDeactivationSchedule(value: string): void;
	toggleInStop(): void;
	toggleIsRestricted(): void;
	toggleIsPreselected(): void;
	toggleForceDisable(): void;
	selectedProperties: TPropertiesOfPatchChangesForModifiers[];
	price: Nullable<number>;
	supplementPrice: Nullable<number>;
	quantityMaxLimit: Nullable<number>;
	activationSchedule: string;
	deactivationSchedule: string;
	hasSchedulePriority: boolean;
	inStop: boolean;
	isRestricted: boolean;
	isPreselected: boolean;
	forceDisable: boolean;
}

export const Cron: React.FC<IProps> = ({
	changePrice,
	changeSupplementPrice,
	changeQuantityMaxLimit,
	changeActivationSchedule,
	changeDeactivationSchedule,
	toggleInStop,
	toggleIsRestricted,
	toggleIsPreselected,
	toggleForceDisable,
	selectedProperties,
	hasSchedulePriority,
	inStop,
	isRestricted,
	isPreselected,
	forceDisable,
}) => {
	return (
		<div className={style.container}>
			{selectedProperties.some((selectedProperty) =>
				[
					'price',
					'supplementPrice',
					'quantityMaxLimit',
					'isDisabled',
					'isRestricted',
					'isPreselected',
					'forceDisable',
				].includes(selectedProperty),
			) && (
				<div className={style.checkboxesWrapper}>
					<div className={style.row}>
						{selectedProperties.includes('price') && (
							<div className={style.input}>
								<PriceInput changePrice={changePrice} />
							</div>
						)}
						{selectedProperties.includes('supplementPrice') && (
							<div className={style.input}>
								<SupplementPriceInput changeSupplementPrice={changeSupplementPrice} />
							</div>
						)}
						{selectedProperties.includes('quantityMaxLimit') && (
							<div className={style.input}>
								<QuantityMaxLimitInput changeQuantityMaxLimit={changeQuantityMaxLimit} />
							</div>
						)}
					</div>
					{selectedProperties.includes('isDisabled') && (
						<div className={style.input}>
							<InStopCheckbox toggleInStop={toggleInStop} inStop={inStop} />
						</div>
					)}
					{selectedProperties.includes('isRestricted') && (
						<div className={style.input}>
							<IsRestrictedCheckbox toggleIsRestricted={toggleIsRestricted} isRestricted={isRestricted} />
						</div>
					)}
					{selectedProperties.includes('isPreselected') && (
						<div className={style.input}>
							<IsPreselectedCheckbox
								toggleIsPreselected={toggleIsPreselected}
								isPreselected={isPreselected}
							/>
						</div>
					)}
					{selectedProperties.includes('forceDisable') && (
						<div className={style.input}>
							<ForceDisableCheckbox toggleForceDisable={toggleForceDisable} forceDisable={forceDisable} />
						</div>
					)}
				</div>
			)}

			{selectedProperties.some((selectedProperty) =>
				['activation', 'deactivation'].includes(selectedProperty),
			) && (
				<div>
					<div className={style.row}>
						{selectedProperties.includes('activation') && (
							<ActivationScheduleInput changeActivationSchedule={changeActivationSchedule} />
						)}
						{selectedProperties.includes('deactivation') && (
							<DeactivationScheduleInput changeDeactivationSchedule={changeDeactivationSchedule} />
						)}
					</div>
					<CronGenerator />
				</div>
			)}

			{selectedProperties.some((selectedProperty) => ['activation', 'deactivation'].includes(selectedProperty)) &&
				hasSchedulePriority && (
					<div className={style.infoWrapper}>
						<svg className={style.icon}>
							<use xlinkHref={`${breadcrumbs}#circleInfoTransparent`} />
						</svg>
						<Text
							translationNameSpace="form"
							translationKey="warningModifierSchedule"
							theme="secondary"
							size="s"
							display="inline"
						/>
					</div>
				)}
		</div>
	);
};
